import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Target,
  Buildings,
  UserCircle,
  Lightbulb,
  ArrowsClockwise,
  UploadSimple,
  Sparkle,
  Warning,
  GearSix,
  TrendUp,
  Shield,
  CheckCircle,
  Megaphone,
  WarningCircle,
  CaretRight,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { materialsApi } from '@/lib/api'
import { sanitizeInsightSummary } from '@/lib/insights'
import { useScrollLock } from '@/lib/useScrollLock'
import { useNavigate } from 'react-router-dom'

interface InsightsData {
  status: string
  executive_summary?: string
  marketing_strategies?: Array<{
    title: string
    description: string
    priority: string
    estimated_impact: string
    metrics_to_track?: string[]
  }>
  target_industries?: Array<{
    industry: string
    reasoning: string
    market_size_indicator: string
    fit_score: number
    approach?: string
  }>
  ideal_icp?: {
    job_titles: string[]
    company_size: string
    pain_points: string[]
    buying_triggers: string[]
    decision_criteria: string[]
    industries: string[]
    objections?: string[]
    messaging_angles?: string[]
  }
  additional_insights?: Array<{
    category: string
    insight: string
    actionable_recommendation: string
  }>
  next_steps?: string[]
  content_gaps?: Array<{
    gap: string
    impact: string
    recommendation: string
  }>
  competitive_positioning?: {
    summary?: string
    differentiators?: string[]
    value_propositions?: string[]
  }
  campaign_suggestions?: Array<{
    name: string
    objective: string
    target_audience: string
    key_message: string
    channels?: string[]
    expected_outcome?: string
  }>
  material_count: number
  generated_at?: string
  is_stale: boolean
}

type Tab = 'strategies' | 'industries' | 'icp' | 'actions' | 'competitive'

const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'strategies', label: 'Strategies', icon: Target },
  { key: 'industries', label: 'Industries', icon: Buildings },
  { key: 'icp', label: 'Ideal ICP', icon: UserCircle },
  { key: 'actions', label: 'Actions', icon: Lightbulb },
  { key: 'competitive', label: 'Competitive', icon: Shield },
]

// Priority is recommendation strength, not error severity. Red would read as a bug.
// high → amber (attention) · medium → indigo accent · low → muted neutral. §3 / §11.
// Stripe rendered as soft top-to-bottom gradient on the left edge — same idiom as
// the dashboard TOP STRATEGIES cards. Hex tints used by both pill and stripe.
// The project amber is #f5a524, not Tailwind's amber-500 #f59e0b — the two were
// mixed in this file, so the priority stripe and the fit-score dot rendered a
// different amber from every other amber surface in the product.
const PROJECT_AMBER = '#f5a524'

const priorityStripeColor: Record<string, string> = {
  high: PROJECT_AMBER,
  medium: '#5e6ad2',
  low: 'rgba(120,120,128,0.45)',
}

const priorityBadge: Record<string, string> = {
  high: 'bg-amber-500/12 text-amber-700 dark:text-amber-300 border-amber-500/20',
  medium: 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border-[#5e6ad2]/20',
  low: 'bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:text-[#8a8f98] border-border/60 dark:border-white/[0.06]',
}

const sizeColors: Record<string, string> = {
  large: 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border-[#5e6ad2]/20',
  medium: 'bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:text-[#8a8f98] border-border/60 dark:border-white/[0.06]',
  niche: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
}

// Mono-eyebrow caption (Berkeley Mono — §2). Used for section labels inside the modal.
const monoEyebrow: React.CSSProperties = {
  fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontWeight: 510,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  fontSize: '10px',
}

const easeOutExpo = [0.16, 1, 0.3, 1] as const

// SVG fit-score ring — colors mapped to design-system semantic palette.
const ScoreRing: React.FC<{ score: number; size?: number }> = ({ score, size = 44 }) => {
  const radius = (size - 6) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (score / 10) * circumference
  const color =
    score >= 8 ? '#10b981' // emerald
      : score >= 6 ? '#5e6ad2' // indigo (brand)
        : score >= 4 ? PROJECT_AMBER
          : '#ef4444' // red

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} strokeWidth="3" stroke="currentColor" fill="none" className="text-muted-foreground/20 dark:text-white/[0.08]" />
        <circle cx={size / 2} cy={size / 2} r={radius} strokeWidth="3" stroke={color} fill="none" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="transition-all duration-700" />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center text-foreground tabular-nums"
        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
      >
        {score}
      </span>
    </div>
  )
}

const SectionEyebrow: React.FC<{ icon?: React.ElementType; iconClass?: string; children: React.ReactNode }> = ({ icon: Icon, iconClass, children }) => (
  <div className="flex items-center gap-1.5 mb-2.5 text-muted-foreground dark:text-[#8a8f98]" style={monoEyebrow}>
    {Icon && <Icon className={`h-3 w-3 ${iconClass ?? ''}`} weight="regular" aria-hidden="true" />}
    {children}
  </div>
)

interface ContentInsightsModalProps {
  isOpen: boolean
  onClose: () => void
}

const ContentInsightsModal: React.FC<ContentInsightsModalProps> = ({ isOpen, onClose }) => {
  useScrollLock(isOpen)
  const navigate = useNavigate()
  const [data, setData] = useState<InsightsData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('strategies')

  useEffect(() => {
    if (isOpen) fetchInsights()
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('strategies')
      setError(null)
    }
  }, [isOpen])

  const fetchInsights = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await materialsApi.getInsights()
      setData(res.data)
    } catch {
      setError('Failed to load insights')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerate = async () => {
    setIsGenerating(true)
    setError(null)
    try {
      const res = await materialsApi.generateInsights()
      setData(res.data)
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to generate insights'
      setError(detail)
    } finally {
      setIsGenerating(false)
    }
  }

  const showResults = data?.status === 'completed' && !isGenerating
  const showNoMaterials = data?.status === 'no_materials' && !isLoading
  const showNotGenerated = data?.status === 'not_generated' || data?.status === 'failed'
  const isApiKeyError = error?.toLowerCase().includes('api key')

  /* ─── Tab: Strategies ─── */
  const renderStrategies = () => {
    const strategies = data?.marketing_strategies || []
    if (!strategies.length) {
      return (
        <p
          className="text-muted-foreground dark:text-[#8a8f98] py-10 text-center"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No strategies generated.
        </p>
      )
    }
    return (
      <div className="space-y-3">
        {strategies.map((s, i) => {
          const stripe = priorityStripeColor[s.priority] || priorityStripeColor.medium
          return (
          <div
            key={i}
            className="relative p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08] overflow-hidden"
          >
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 w-px"
              style={{
                background: `linear-gradient(180deg, ${stripe} 0%, color-mix(in srgb, ${stripe} 20%, transparent) 45%, transparent 100%)`,
              }}
            />
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-start gap-2.5 min-w-0">
                <span
                  className="flex items-center justify-center h-6 w-6 rounded-full bg-muted/60 dark:bg-white/[0.04] dark:border dark:border-white/[0.06] shrink-0 mt-0.5 text-foreground dark:text-[#f7f8f8] tabular-nums"
                  style={{ fontSize: '11px', fontWeight: 590 }}
                >
                  {i + 1}
                </span>
                <h4
                  className="text-foreground dark:text-[#f7f8f8] min-w-0"
                  style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px', lineHeight: 1.4 }}
                >
                  {s.title}
                </h4>
              </div>
              <span
                className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border whitespace-nowrap ${priorityBadge[s.priority] || priorityBadge.medium}`}
                style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                {s.priority}
              </span>
            </div>
            <p
              className="text-muted-foreground dark:text-[#d0d6e0] ml-[34px]"
              style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
            >
              {s.description}
            </p>
            {s.estimated_impact && (
              <p
                className="text-[#5e6ad2] dark:text-[#a5b4fc] flex items-center gap-1.5 mt-2 ml-[34px]"
                style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <TrendUp className="h-3.5 w-3.5" weight="regular" />
                {s.estimated_impact}
              </p>
            )}
            {s.metrics_to_track && s.metrics_to_track.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5 ml-[34px]">
                {s.metrics_to_track.map((m, j) => (
                  <span
                    key={j}
                    className="px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/15"
                    style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}
          </div>
          )
        })}
      </div>
    )
  }

  /* ─── Tab: Industries ─── */
  const renderIndustries = () => {
    const industries = [...(data?.target_industries || [])].sort((a, b) => b.fit_score - a.fit_score)
    if (!industries.length) {
      return (
        <p
          className="text-muted-foreground dark:text-[#8a8f98] py-10 text-center"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No industries generated.
        </p>
      )
    }
    return (
      <div className="space-y-3">
        {industries.map((ind, i) => (
          <div
            key={i}
            className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]"
          >
            <div className="flex items-start gap-3">
              <ScoreRing score={ind.fit_score} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <h4
                    className="text-foreground dark:text-[#f7f8f8] min-w-0 truncate"
                    style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    {ind.industry}
                  </h4>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border whitespace-nowrap ${sizeColors[ind.market_size_indicator] || sizeColors.medium}`}
                    style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    {ind.market_size_indicator}
                  </span>
                </div>
                <p
                  className="text-muted-foreground dark:text-[#d0d6e0] mb-2.5"
                  style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                >
                  {ind.reasoning}
                </p>
                {ind.approach && (
                  <div className="p-2.5 rounded-md bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/15">
                    <p
                      className="text-muted-foreground dark:text-[#d0d6e0]"
                      style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                    >
                      <span
                        className="text-[#5e6ad2] dark:text-[#a5b4fc]"
                        style={{ fontWeight: 590 }}
                      >
                        Approach.{' '}
                      </span>
                      {ind.approach}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  /* ─── Tab: Ideal ICP ─── */
  const renderICP = () => {
    const icp = data?.ideal_icp
    if (!icp) {
      return (
        <p
          className="text-muted-foreground dark:text-[#8a8f98] py-10 text-center"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No ICP data generated.
        </p>
      )
    }

    return (
      <div className="space-y-3">
        {/* Persona header */}
        <div className="p-4 rounded-lg border bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/15">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-md bg-[#5e6ad2]/15 border border-[#5e6ad2]/20 flex items-center justify-center shrink-0">
              <UserCircle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
            </div>
            <div className="min-w-0">
              <h4
                className="text-foreground dark:text-[#f7f8f8] truncate"
                style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
              >
                Ideal Customer Profile
              </h4>
              {icp.company_size && (
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                  style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
                >
                  {icp.company_size}
                </p>
              )}
            </div>
          </div>
          {(icp.job_titles?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {icp.job_titles.map((t, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/15"
                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {(icp.industries?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Target industries</SectionEyebrow>
            <div className="flex flex-wrap gap-1.5">
              {icp.industries.map((ind, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full bg-muted/60 text-foreground/80 dark:bg-white/[0.04] dark:text-[#d0d6e0] border border-border/50 dark:border-white/[0.06]"
                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  {ind}
                </span>
              ))}
            </div>
          </div>
        )}

        {(icp.pain_points?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Pain points</SectionEyebrow>
            <ul className="space-y-1.5">
              {icp.pain_points.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-muted-foreground dark:text-[#d0d6e0]"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  <span className="h-1 w-1 rounded-full bg-red-500/70 dark:bg-red-400/70 shrink-0 mt-2" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(icp.buying_triggers?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Buying triggers</SectionEyebrow>
            <ul className="space-y-1.5">
              {icp.buying_triggers.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-muted-foreground dark:text-[#d0d6e0]"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  <span className="h-1 w-1 rounded-full bg-emerald-500/80 dark:bg-emerald-400/80 shrink-0 mt-2" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(icp.decision_criteria?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Decision criteria</SectionEyebrow>
            <ul className="space-y-1.5">
              {icp.decision_criteria.map((d, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-muted-foreground dark:text-[#d0d6e0]"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  <CheckCircle className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc] shrink-0 mt-0.5" weight="regular" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(icp.objections?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Common objections</SectionEyebrow>
            <ul className="space-y-1.5">
              {icp.objections!.map((o, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-muted-foreground dark:text-[#d0d6e0]"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  <span className="h-1 w-1 rounded-full bg-amber-500/80 dark:bg-amber-400/80 shrink-0 mt-2" />
                  {o}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(icp.messaging_angles?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Messaging angles</SectionEyebrow>
            <ul className="space-y-1.5">
              {icp.messaging_angles!.map((m, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-muted-foreground dark:text-[#d0d6e0]"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  <span className="h-1 w-1 rounded-full bg-[#5e6ad2]/70 dark:bg-[#7170ff]/70 shrink-0 mt-2" />
                  {m}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  /* ─── Tab: Actions ─── */
  const renderActions = () => {
    const nextSteps = data?.next_steps || []
    const insights = data?.additional_insights || []
    const contentGaps = data?.content_gaps || []
    const campaigns = data?.campaign_suggestions || []
    const hasContent = nextSteps.length > 0 || insights.length > 0 || contentGaps.length > 0 || campaigns.length > 0

    if (!hasContent) {
      return (
        <p
          className="text-muted-foreground dark:text-[#8a8f98] py-10 text-center"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No action items generated.
        </p>
      )
    }

    return (
      <div className="space-y-5">
        {nextSteps.length > 0 && (
          <div>
            <SectionEyebrow icon={CheckCircle} iconClass="text-emerald-600 dark:text-emerald-400">
              Immediate next steps
            </SectionEyebrow>
            <div className="space-y-2">
              {nextSteps.map((step, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 p-3 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.06]"
                >
                  <span
                    className="flex items-center justify-center h-5 w-5 rounded-full shrink-0 mt-0.5 text-white tabular-nums"
                    style={{ background: 'rgb(16,185,129)', fontSize: '10px', fontWeight: 590 }}
                  >
                    {i + 1}
                  </span>
                  <p
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                  >
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {campaigns.length > 0 && (
          <div>
            <SectionEyebrow icon={Megaphone} iconClass="text-[#5e6ad2] dark:text-[#a5b4fc]">
              Campaign ideas
            </SectionEyebrow>
            <div className="space-y-3">
              {campaigns.map((c, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08] hover:border-[#5e6ad2]/30 dark:hover:border-[#7170ff]/30 transition-colors duration-150"
                >
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <h5
                      className="text-foreground dark:text-[#f7f8f8] min-w-0"
                      style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                    >
                      {c.name}
                    </h5>
                    <motion.button
                      onClick={() => { navigate('/campaigns'); onClose() }}
                      className="group inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0] shrink-0 text-white transition-colors duration-150"
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      whileTap={{ scale: 0.97 }}
                    >
                      Create
                      <CaretRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" weight="bold" />
                    </motion.button>
                  </div>
                  <p
                    className="text-muted-foreground dark:text-[#d0d6e0] mb-2.5"
                    style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                  >
                    {c.objective}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="p-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.06]">
                      <p className="text-muted-foreground dark:text-[#8a8f98] mb-1" style={monoEyebrow}>Audience</p>
                      <p className="text-foreground dark:text-[#d0d6e0]" style={{ fontSize: '12px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
                        {c.target_audience}
                      </p>
                    </div>
                    <div className="p-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.06]">
                      <p className="text-muted-foreground dark:text-[#8a8f98] mb-1" style={monoEyebrow}>Message</p>
                      <p className="text-foreground dark:text-[#d0d6e0]" style={{ fontSize: '12px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
                        {c.key_message}
                      </p>
                    </div>
                  </div>
                  {c.channels && c.channels.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {c.channels.map((ch, j) => (
                        <span
                          key={j}
                          className="px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:text-[#8a8f98] border border-border/50 dark:border-white/[0.06]"
                          style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em' }}
                        >
                          {ch}
                        </span>
                      ))}
                    </div>
                  )}
                  {c.expected_outcome && (
                    <p
                      className="text-[#5e6ad2] dark:text-[#a5b4fc] flex items-center gap-1.5 mt-2.5"
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <TrendUp className="h-3.5 w-3.5" weight="regular" />
                      {c.expected_outcome}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {contentGaps.length > 0 && (
          <div>
            <SectionEyebrow icon={WarningCircle} iconClass="text-amber-600 dark:text-amber-400">
              Content gaps
            </SectionEyebrow>
            <div className="space-y-2">
              {contentGaps.map((gap, i) => (
                <div
                  key={i}
                  className="p-3 rounded-md border bg-amber-500/[0.06] dark:bg-amber-500/[0.08] border-amber-500/15"
                >
                  <p
                    className="text-foreground dark:text-[#f7f8f8] mb-1"
                    style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    {gap.gap}
                  </p>
                  <p
                    className="text-muted-foreground dark:text-[#d0d6e0] mb-2"
                    style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                  >
                    {gap.impact}
                  </p>
                  <div className="flex items-start gap-2 p-2 rounded-md bg-background dark:bg-white/[0.03] border border-border/50 dark:border-white/[0.06]">
                    <Lightbulb className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" weight="regular" />
                    <p
                      className="text-foreground dark:text-[#d0d6e0]"
                      style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                    >
                      {gap.recommendation}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {insights.length > 0 && (
          <div>
            <SectionEyebrow icon={Lightbulb} iconClass="text-amber-600 dark:text-amber-400">
              Additional insights
            </SectionEyebrow>
            <div className="space-y-3">
              {insights.map((ins, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]"
                >
                  <span
                    className="inline-flex items-center px-2 py-0.5 mb-2 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/15"
                    style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    {ins.category}
                  </span>
                  <p
                    className="text-muted-foreground dark:text-[#d0d6e0] mb-2.5"
                    style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                  >
                    {ins.insight}
                  </p>
                  <div className="p-2.5 rounded-md bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08] border border-emerald-500/15">
                    <p
                      className="text-emerald-700 dark:text-emerald-300"
                      style={{ fontSize: '12px', fontWeight: 510, lineHeight: 1.55, letterSpacing: '-0.13px' }}
                    >
                      {ins.actionable_recommendation}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  /* ─── Tab: Competitive ─── */
  const renderCompetitive = () => {
    const cp = data?.competitive_positioning
    if (!cp || (!cp.summary && !cp.differentiators?.length && !cp.value_propositions?.length)) {
      return (
        <p
          className="text-muted-foreground dark:text-[#8a8f98] py-10 text-center"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No competitive positioning data generated.
        </p>
      )
    }

    return (
      <div className="space-y-3">
        {cp.summary && (
          <div className="p-4 rounded-lg border bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/15">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-[#5e6ad2]/15 border border-[#5e6ad2]/20 flex items-center justify-center shrink-0">
                <Shield className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
              </div>
              <div className="min-w-0">
                <p
                  className="text-[#5e6ad2] dark:text-[#a5b4fc] mb-1"
                  style={monoEyebrow}
                >
                  Positioning summary
                </p>
                <p
                  className="text-foreground dark:text-[#d0d6e0]"
                  style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                >
                  {cp.summary}
                </p>
              </div>
            </div>
          </div>
        )}

        {(cp.differentiators?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Key differentiators</SectionEyebrow>
            <div className="space-y-2">
              {cp.differentiators!.map((d, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="h-5 w-5 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-400" weight="regular" />
                  </span>
                  <p
                    className="text-foreground dark:text-[#d0d6e0]"
                    style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                  >
                    {d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(cp.value_propositions?.length ?? 0) > 0 && (
          <div className="p-4 rounded-lg border bg-card dark:bg-white/[0.02] border-border/60 dark:border-white/[0.08]">
            <SectionEyebrow>Core value propositions</SectionEyebrow>
            <div className="space-y-2">
              {cp.value_propositions!.map((v, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 p-3 rounded-md bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/15"
                >
                  <span
                    className="flex items-center justify-center h-5 w-5 rounded-full bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/20 shrink-0 tabular-nums"
                    style={{ fontSize: '10px', fontWeight: 590 }}
                  >
                    {i + 1}
                  </span>
                  <p
                    className="text-foreground dark:text-[#d0d6e0]"
                    style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                  >
                    {v}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const tabContent: Record<Tab, () => React.ReactNode> = {
    strategies: renderStrategies,
    industries: renderIndustries,
    icp: renderICP,
    actions: renderActions,
    competitive: renderCompetitive,
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
      <div key="content-insights" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop — blur-only, no dark overlay. Covers scrollbar gutter (§14, §16, §29) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed top-0 left-0 w-screen h-screen backdrop-blur-md"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* Card */}
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="content-insights-title"
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.18, ease: easeOutExpo }}
          className="relative z-10 w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl bg-card border border-border/60 dark:bg-[#0f1011] dark:border-white/[0.08] shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border/50 dark:border-white/[0.06]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.18] border border-[#5e6ad2]/15 dark:border-[#5e6ad2]/25 flex items-center justify-center shrink-0">
                <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
              </div>
              <div className="min-w-0">
                <h2
                  id="content-insights-title"
                  className="text-foreground dark:text-[#f7f8f8] truncate"
                  style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
                >
                  AI Content Insights
                </h2>
                {data?.material_count ? (
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                    style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
                  >
                    Based on {data.material_count} material{data.material_count !== 1 ? 's' : ''}
                    {data?.generated_at && ` · Generated ${new Date(data.generated_at).toLocaleDateString()}`}
                  </p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close insights"
              className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] hover:bg-muted/60 dark:hover:bg-white/[0.04] transition-colors duration-150"
            >
              <X className="h-4 w-4" weight="regular" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {(isLoading || isGenerating) && (
              <div className="flex flex-col items-center justify-center py-16">
                <LoadingSpinner size="lg" />
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mt-4"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                >
                  {isGenerating ? 'Analyzing your marketing materials with AI…' : 'Loading insights…'}
                </p>
                {isGenerating && (
                  <p
                    className="text-muted-foreground/80 dark:text-[#62666d] mt-1"
                    style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
                  >
                    This may take up to a minute.
                  </p>
                )}
              </div>
            )}

            {error && !isLoading && !isGenerating && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-12 w-12 rounded-md bg-amber-500/15 border border-amber-500/25 flex items-center justify-center mb-4">
                  <Warning className="h-5 w-5 text-amber-600 dark:text-amber-400" weight="regular" />
                </div>
                <p
                  className="text-muted-foreground dark:text-[#d0d6e0] text-center max-w-sm mb-4"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  {error}
                </p>
                {isApiKeyError ? (
                  <Button variant="outline" onClick={() => { navigate('/settings'); onClose() }}>
                    <GearSix className="h-4 w-4 mr-2" weight="regular" />
                    Go to Settings
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handleGenerate}>
                    <ArrowsClockwise className="h-4 w-4 mr-2" weight="regular" />
                    Try again
                  </Button>
                )}
              </div>
            )}

            {showNoMaterials && !error && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
                  <UploadSimple className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                </div>
                <h3
                  className="text-foreground dark:text-[#f7f8f8] mb-2"
                  style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.24px' }}
                >
                  No materials yet
                </h3>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] text-center max-w-sm mb-6"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  Upload marketing materials first to generate AI-powered insights.
                </p>
                <Button variant="default" onClick={() => { navigate('/materials'); onClose() }}>
                  <UploadSimple className="h-4 w-4 mr-2" weight="regular" />
                  Upload materials
                </Button>
              </div>
            )}

            {showNotGenerated && !isLoading && !isGenerating && !error && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-12 w-12 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.18] border border-[#5e6ad2]/15 dark:border-[#5e6ad2]/25 flex items-center justify-center mb-4">
                  <Sparkle className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                </div>
                <h3
                  className="text-foreground dark:text-[#f7f8f8] mb-2"
                  style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.24px' }}
                >
                  Generate Content Insights
                </h3>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] text-center max-w-md mb-6"
                  style={{ fontSize: '13px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                >
                  Analyze your {data?.material_count || 0} marketing material
                  {(data?.material_count || 0) !== 1 ? 's' : ''} to get AI-powered strategies,
                  campaigns, ICP, and competitive positioning.
                </p>
                <Button variant="default" onClick={handleGenerate}>
                  <Sparkle className="h-4 w-4 mr-2" weight="regular" />
                  Generate insights
                </Button>
              </div>
            )}

            {showResults && !isLoading && !error && (
              <div className="space-y-4">
                {/* Mirrors the dashboard card's stale banner. Body copy is a text
                    token, not amber: `text-amber-200` on this surface is under AA
                    at 12px in dark mode. The amber stays in the icon and border. */}
                {data?.is_stale && (
                  <div
                    role="status"
                    className="flex items-center gap-3 px-3.5 py-2.5 rounded-md bg-amber-500/[0.08] border border-amber-500/25 dark:bg-amber-500/[0.10] dark:border-amber-500/30"
                  >
                    <Warning className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" weight="fill" />
                    <p
                      className="text-foreground/80 dark:text-[#d0d6e0] flex-1"
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      New materials uploaded since last analysis. Regenerate for updated insights.
                    </p>
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c5cbff] disabled:opacity-50 transition-colors duration-150"
                      style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                    >
                      Regenerate
                    </button>
                  </div>
                )}

                {/* Sanitized so legacy raw-JSON blobs never render (21572) */}
                {sanitizeInsightSummary(data?.executive_summary) && (
                  <div className="p-4 rounded-lg border bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/15">
                    <p
                      className="text-[#5e6ad2] dark:text-[#a5b4fc] mb-1.5"
                      style={monoEyebrow}
                    >
                      Executive summary
                    </p>
                    <p
                      className="text-foreground dark:text-[#d0d6e0]"
                      style={{ fontSize: '13px', lineHeight: 1.65, letterSpacing: '-0.13px' }}
                    >
                      {sanitizeInsightSummary(data?.executive_summary)}
                    </p>
                  </div>
                )}

                {/* Tabs */}
                <div className="flex border-b border-border/50 dark:border-white/[0.06] -mx-1 px-1">
                  {tabs.map(tab => {
                    const isActive = activeTab === tab.key
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`group inline-flex items-center gap-1.5 px-3 py-2.5 -mb-px border-b-2 transition-colors duration-150 whitespace-nowrap ${
                          isActive
                            ? 'border-[#5e6ad2] text-[#5e6ad2] dark:border-[#7170ff] dark:text-[#a5b4fc]'
                            : 'border-transparent text-muted-foreground dark:text-[#8a8f98] hover:text-foreground dark:hover:text-[#f7f8f8]'
                        }`}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        <tab.icon className="h-3.5 w-3.5" weight="regular" aria-hidden="true" />
                        {tab.label}
                      </button>
                    )
                  })}
                </div>

                {/* Tab Content */}
                <div className="min-h-[200px] pt-1">{tabContent[activeTab]()}</div>
              </div>
            )}
          </div>

          {/* Footer — both CTAs anchored to brand palette §3/§7 (consistent across modes) */}
          {showResults && (
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50 dark:border-white/[0.06]">
              <motion.button
                type="button"
                onClick={onClose}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center h-9 px-4 rounded-md border text-foreground bg-background border-border/60 hover:bg-muted/50 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14] transition-colors duration-150"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                Close
              </motion.button>
              <motion.button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0] text-white transition-colors duration-150 disabled:opacity-60 disabled:pointer-events-none"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <ArrowsClockwise
                  className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`}
                  weight="regular"
                />
                Regenerate
              </motion.button>
            </div>
          )}
        </motion.div>
      </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

export default ContentInsightsModal
